import React, { useState, useContext } from "react";
import { Box, TextField, Button, Typography, Paper } from "@mui/material";
import LayoutAccessManagement from "components/structure/LayoutAccessManagement";
import { useNavigate } from "react-router-dom";
import RoleService from "services/RoleService";
import { SnackbarContext } from "context/snackBarContext";

const ReadOnlyUserMapping = ({ onSave, onCancel }) => {
  const [userCode, setUserCode] = useState("");
  const [readOnlyRole, setReadOnlyRole] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { setOpenAlert, setSeverity, setMessage } = useContext(SnackbarContext);

  const validate = () => {
    const e = {};
    if (!userCode.trim()) e.userCode = "Required";
    if (!readOnlyRole.trim()) e.readOnlyRole = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const payload = {
      readUserBatOneId: readOnlyRole.trim(),
      realUserBatOneId: userCode.trim(),
    };
    try {
      setSaving(true);
      const res = await RoleService.saveReadOnlyUserMapping(payload);
      // Optional: bubble up success
      onSave && onSave({ payload, response: res?.data });
      // Reset fields
      setUserCode("");
      setReadOnlyRole("");
      // Show success snackbar
      setSeverity("success");
      setMessage("Mapping successfully saved");
      setOpenAlert(true);
    } catch (e) {
      // Surface API error message
      setErrors((prev) => ({
        ...prev,
        api: e?.response?.data?.message || e?.message || "Failed to save mapping",
      }));
      console.error("saveReadOnlyUserMapping failed:", e);
      // Show error snackbar
      setSeverity("error");
      setMessage(e?.response?.data?.message || e?.message);
      setOpenAlert(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <LayoutAccessManagement>
      <Box sx={{ paddingBottom: "25px" }}></Box>
      <Box sx={{ p: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: "1px solid #E7E8EB",
            borderRadius: "16px",
            display: "flex",
            flexDirection: "column",
            gap: 3,
            background: "#FFFFFF"
          }}
        >
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontSize: "18px",
              fontWeight: 600,
              color: "#102A62",
              mb: 1
            }}
          >
            Read Only User Mapping
          </Typography>
          
          {/* Row with two input groups */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 4,
              flexWrap: "wrap"
            }}
          >
            <Box sx={{ flex: 1, minWidth: 220, display: "flex", flexDirection: "column" }}>
              <Typography
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#102A62",
                  mb: 0.5
                }}
              >
                Read Only User
              </Typography>
              <TextField
                value={readOnlyRole}
                onChange={(e) => setReadOnlyRole(e.target.value)}
                size="small"
                placeholder="Enter BAT One ID"
                error={!!errors.readOnlyRole}
                helperText={errors.readOnlyRole}
                InputProps={{
                  sx: {
                    borderRadius: "12px",
                    fontFamily: "Montserrat"
                  }
                }}
              />
            </Box>

            <Box sx={{ flex: 1, minWidth: 220, display: "flex", flexDirection: "column" }}>
              <Typography
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#102A62",
                  mb: 0.5
                }}
              >
                Real User
              </Typography>
              <TextField
                value={userCode}
                onChange={(e) => setUserCode(e.target.value)}
                size="small"
                placeholder="Enter BAT One ID"
                error={!!errors.userCode}
                helperText={errors.userCode}
                InputProps={{
                  sx: {
                    borderRadius: "12px",
                    fontFamily: "Montserrat"
                  }
                }}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 1 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setUserCode("");
                setReadOnlyRole("");
                navigate(-1)
              }}
              sx={{
                textTransform: "none",
                fontFamily: "Montserrat",
                borderColor: "#62636C",
                color: "#62636C",
                "&:hover": { borderColor: "#62636C", background: "#F5F5F5" }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{
                backgroundColor: "#004F9F",
                textTransform: "none",
                fontFamily: "Montserrat",
                "&:hover": { backgroundColor: "#004F9F" }
              }}
            >
              {saving ? "Saving..." : "Ok"}
            </Button>
          </Box>
        </Paper>
      </Box>
    </LayoutAccessManagement>
  );
};

export default ReadOnlyUserMapping;